import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../../redux/hooks';
import DateRangeInput from '../../../components/custom-input/DateInputRange';
import { FilterOption } from '../../../components/custom-input/FilterDropdown';
import SearchBy from '../../../components/search-by/SearchBy';
import { AlertSVG, CancelSVG, DownloadSVG } from '../../../components/svgs';
import PagedTable from '../../../components/sorted-table/PagedTable';
import ActionMenu from '../../../components/action-buttons/ActionButtons';
import { Loader } from '../../../components';
import { TransactionFilterOption } from '../../../components/transaction/TransactionsList';
import InsuredCustomerDetailsModal from './insuredCustomerDetailsModal';
import { InsuredCustomer } from '../../../types/interfaces/reports.interface';
import { Account, PagedResponse, StandardParamsToODataParams } from '../../../types/interfaces';
import { getAccounts } from '../../../api/accountApi';
import { downloadInsuredCustomersReport, getInsuredCustomersOdata } from '../../../api/insuredCustomersApi';
import { showToast } from '../../../services/toast.service';
import { centsToDollars } from '../../../services/account.service';
import { toastMessages } from '../../../constants/errorMessages';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS, TABLE_HEADER_CONSTANTS, VALIDATION_ALERT_CONSTANTS } from '../../../constants/common';
import { dayAfterDate } from '../../../utils/dateHandler';
import { formatDateToShortMonthDayYear } from '../../../utils/commonUtils';
import './insuredCustomersReport.scss';

const InsuredCustomersReport = (): JSX.Element => {
  const user = useAppSelector(state => state.user);

  const [account, setAccount] = useState<Account>();
  const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>({
    startDate: null,
    endDate: null,
  });
  const [downloadingReport, setDownloadingReport] = useState<boolean>(true);
  const [filter, setFilter] = useState<string>();
  const [insuredCustomerData, setInsuredCustomerData] = useState<InsuredCustomer[]>([]);
  const [isInsuredCustomerDetailsModalOpen, setIsInsuredCustomerDetailsModalOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [offset, setOffset] = useState<number>(0);
  const [removedFilter, setRemovedFilter] = useState<{ criteria: FilterOption<any>; query: string }>();
  const [searches, setSearches] = useState<{ criteria: FilterOption<any>; query: string }[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<InsuredCustomer>();
  const [showHighlight, setShowHighlight] = useState(false);

  const isDateRangeSelected = dateRange.startDate && dateRange.endDate;
  const fetchLimit = 20;
  const callNumber: MutableRefObject<number> = useRef<number>(0);
  const hasAllResults: MutableRefObject<boolean> = useRef<boolean>(false);
  const isLoadingAdditionalPage: MutableRefObject<boolean> = useRef<boolean>(false);
  const ref = useRef<any>(null);

  const searchCriteriaOptions: FilterOption<TransactionFilterOption>[] = [
    { name: `${TABLE_HEADER_CONSTANTS.CLIENT_NAME}`, value: { parameter: 'accountId' } },
    { name: `${TABLE_HEADER_CONSTANTS.TRANSACTION_ID}`, value: { parameter: 'bulkOrderId', isNumber: true } },
  ];

  useEffect(() => {
    hasAllResults.current = false;
    setInsuredCustomerData([]);
    if (offset === 0) {
      window.innerWidth <= 767 && loadData();
    } else {
      setOffset(0);
      if ('current' in ref) {
        ref.current?.scroll({ top: 0 });
      }
    }
  }, [filter]);

  useEffect(() => {
    window.innerWidth <= 767 && loadData();
  }, [offset]);

  useEffect(() => {
    window.addEventListener('touchmove', handleScroll);
    return () => window.removeEventListener('touchmove', handleScroll);
  }, []);

  useEffect(() => {
    isLoadingAdditionalPage.current = false;
  }, [insuredCustomerData]);

  const loadData = async () => {
    callNumber.current++;
    if (hasAllResults.current) {
      return;
    }
    const callNum = callNumber.current;
    try {
      setLoading(true);
      const query = { include: 'Account', filter: filter, sort: '^billedDate', limit: fetchLimit, offset: offset };
      const response = await getInsuredCustomersOdata(StandardParamsToODataParams(query));
      const insuredCustomers = response.data;

      if (offset === 0 && 'current' in ref) {
        ref.current?.scroll({ top: 0 });
      }

      if (callNum === callNumber.current) {
        const newTableData = offset > 0 ? [...insuredCustomerData, ...insuredCustomers] : insuredCustomers;
        setInsuredCustomerData(newTableData);
        setLoading(false);
        hasAllResults.current = insuredCustomers.length === 0;
      }
    } catch (error: any) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  const loadMore = () => {
    setOffset(prevState => prevState + fetchLimit);
  };

  const handleScroll = () => {
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
    const position = scrollHeight - scrollTop;
    const atBottom = position - clientHeight <= clientHeight;

    if (atBottom && !isLoadingAdditionalPage.current) {
      isLoadingAdditionalPage.current = true;
      loadMore();
    }
  };

  const handleDateChange = (dateRange: { startDate: Date | null; endDate: Date | null }) => {
    setDateRange(dateRange);
    setShowHighlight(false);
  };

  const handleRemoveFilter = (index: number) => {
    const updatedSearch = [...searches];
    const removedFilter = updatedSearch.splice(index, 1)[0];

    if (removedFilter.criteria.name === 'Client Name') {
      setAccount(undefined);
    }
    setRemovedFilter(removedFilter);
    setSearches(updatedSearch);
  };

  const handleViewDetails = (selectedItem: InsuredCustomer) => {
    setIsInsuredCustomerDetailsModalOpen(true);
    setSelectedCustomer(selectedItem);
  };

  const updateFilters = useCallback(async () => {
    let filters: string[] = [];

    if (dateRange.startDate && dateRange.endDate) {
      let dateFilter = '';
      const { startDate, endDate } = dateRange;
      startDate?.setHours(0);
      endDate?.setHours(0);
      if (startDate && endDate) {
        dateFilter = `billedDate ge ${startDate?.toISOString()} and billedDate lt ${dayAfterDate(endDate).toISOString()}`;
      } else {
        dateFilter = '';
      }
      filters = [...filters, dateFilter];
    }

    for (const search of searches) {
      const minSearchLength = 2;
      if (search.query.length > 0) {
        if (search.criteria.value.parameter === 'accountId' && search.query.length >= minSearchLength) {
          try {
            const maxResults = 5;
            const { data: accountsResponse } = await getAccounts(undefined, {
              filter:
                'contactEmail like ' +
                search.query +
                '||name like ' +
                search.query +
                '||contactFirstName like ' +
                search.query +
                '||contactLastName like ' +
                search.query,
              limit: maxResults,
            });
            const accounts: Account[] = (accountsResponse as PagedResponse<Account>).result;
            if (accounts.length === 0) {
              setFilter('false');
              return;
            } else if (accounts.length === 1) {
              setAccount(accounts[0]);
            } else {
              setAccount(undefined);
            }

            const accountFilters = `(${accounts.map(acc => `accountId eq ${acc.accountId}`).join(' or ')})`;
            filters = [...filters, accountFilters];
          } catch (error) {
            setFilter('false');
            return;
          }
        } else {
          if (search.criteria.value.isNumber === true) {
            if ((+search.query).toString() === 'NaN') {
              setFilter('false');
              return;
            } else {
              filters = [...filters, `${search.criteria.value.parameter} eq ${+search.query}`];
            }
          } else if (search.query.length >= minSearchLength) {
            filters = [...filters, `contains(${search.criteria.value.parameter}, '${search.query}')`];
          }
        }
      }
    }

    const rawFilterString = filters.length > 1 ? `(${filters.join(') and (')})` : filters.join('');
    const filterString = rawFilterString.replace(/'/g, '');
    setFilter(filterString);
  }, [dateRange, searches]);

  useEffect(() => {
    updateFilters();
  }, [updateFilters]);

  const handleDownloadClick = () => {
    if (!isDateRangeSelected) {
      setShowHighlight(true);
    } else {
      handleDownloadReport();
    }
  };

  const getODataParams = () => {
    return StandardParamsToODataParams({
      filter: filter ? filter.concat(' and ', 'InsuredAmountCents gt 0') : 'InsuredAmountCents gt 0',
      sort: '^' + 'billedDate',
    });
  };

  const generateReportPayload = () => {
    const startDate = formatDateToShortMonthDayYear(dateRange.startDate);
    const endDate = formatDateToShortMonthDayYear(dateRange.endDate);

    const searchFilters = {
      'Client Name :': account ? account.name : searches.find(search => search.criteria.name === 'Client Name')?.query,
      'Bulk Transaction Id :': searches.find(search => search.criteria.name === 'Transaction ID')?.query,
      'Report Period :': `${startDate} - ${endDate}`,
    };

    const adminInfo = { 'Created By :': `${user.firstName} ${user.lastName}`, 'User Name :': user.username };

    return { ...adminInfo, ...searchFilters };
  };

  const setReportName = (link: HTMLAnchorElement) => {
    const startDate = formatDateToShortMonthDayYear(dateRange.startDate);
    const endDate = formatDateToShortMonthDayYear(dateRange.endDate);

    const prefix = 'InsuredCustomers';
    const reportPeriod = `${startDate} - ${endDate}`;
    const fileName = account ? `${prefix}_${account.name}_${reportPeriod}.xlsx` : `${prefix}_${reportPeriod}.xlsx`;
    link.setAttribute('download', fileName);
  };

  const handleDownloadReport = async () => {
    if (!downloadingReport) return;

    try {
      setDownloadingReport(false);

      const response = await downloadInsuredCustomersReport(generateReportPayload(), getODataParams());
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      setReportName(link);
      document.body.appendChild(link);
      link.click();
    } catch (error: any) {
      showToast.error(toastMessages.FAILED_TO_DOWNLOAD_REPORT);
    } finally {
      setDownloadingReport(true);
    }
  };

  return (
    <div className="insured-customers-report">
      <div className="header">
        <h2 className="report-header">{LABEL_CONSTANTS.INSURED_CUSTOMERS_REPORT}</h2>
        <div className="search-stack">
          <div className="search">
            <SearchBy
              searches={searches}
              setSearches={searches => setSearches(searches)}
              searchCriteriaOptions={searchCriteriaOptions}
              removedFilter={removedFilter}
            />
          </div>
        </div>
      </div>

      <hr className="header-hr" />
      <div className="selected-filters-summary">
        {searches.map((item, index) => (
          <div key={index} className="filter-summary">
            <span>
              {item.criteria.name}: <strong>{item.query}</strong>
            </span>
            <button onClick={() => handleRemoveFilter(index)} className="remove-filter-btn">
              <CancelSVG />
            </button>
          </div>
        ))}
      </div>
      <div className="filter-row">
        <div className="button-row">
          <div className="filters">
            <div className="filter-category">
              <div className="date-range-input">
                <DateRangeInput value={dateRange} onChange={handleDateChange} isClearable rangeLimit={1} />
              </div>
              {showHighlight && (
                <div className="validated-input-message-error">
                  <AlertSVG />
                  {VALIDATION_ALERT_CONSTANTS.REQUIRED_FIELD}
                </div>
              )}
            </div>
          </div>
          <button
            className={`button outlined download-report ${!isDateRangeSelected ? 'disabled' : ''}`}
            onClick={handleDownloadClick}>
            <DownloadSVG />
            &nbsp; {BUTTON_CONSTANTS.DOWNLOAD_CSV}
          </button>
        </div>
      </div>

      <InsuredCustomerDetailsModal
        isOpen={isInsuredCustomerDetailsModalOpen}
        onClose={() => setIsInsuredCustomerDetailsModalOpen(false)}
        shippingTransactionId={selectedCustomer?.shippingPaymentTransactionId}
      />

      <div className="desk-insured-customers-table">
        <PagedTable
          headers={[
            { displayName: TABLE_HEADER_CONSTANTS.CLIENT_NAME },
            { displayName: TABLE_HEADER_CONSTANTS.DATE_SUBMITTED },
            { displayName: TABLE_HEADER_CONSTANTS.BULK_TRANSACTION_ID },
            { displayName: TABLE_HEADER_CONSTANTS.INSURANCE_AMOUNT },
            { displayName: TABLE_HEADER_CONSTANTS.INSURANCE_PREMIUM },
            { displayName: TABLE_HEADER_CONSTANTS.ACTION },
          ]}
          include="Account"
          sortBy="^billedDate"
          filter={filter}
          getData={params => getInsuredCustomersOdata(StandardParamsToODataParams(params))}
          buildRow={customer => {
            return [
              customer.account.name,
              `${new Date(customer?.billedDate).toLocaleDateString('en-US')}`,
              `${customer.bulkOrderId}`,
              `$${centsToDollars(customer?.insuredAmount)}`,
              `$${centsToDollars(customer?.insurancePremium)}`,
              <ActionMenu
                actionButtons={[{ name: `${BUTTON_CONSTANTS.VIEW_DETAIL}`, action: () => handleViewDetails(customer) }]}
              />,
            ];
          }}
        />
      </div>

      <div className="xs-insured-customers-table">
        {!loading && insuredCustomerData.length === 0 ? (
          <div className="result-item">{`${LABEL_CONSTANTS.NO_RESULTS_FOUND}.`}</div>
        ) : (
          insuredCustomerData?.length > 0 &&
          insuredCustomerData &&
          insuredCustomerData.map((insuredCustomer, index) => {
            return (
              <div className="card max-width" key={'animal-' + insuredCustomer?.shippingPaymentTransactionId + index}>
                <div className="insured-customers">
                  <div className="insured-customers-content">
                    <div className="card-content-section">
                      <div className="left">
                        <label>{`${TABLE_HEADER_CONSTANTS.CLIENT_NAME} :`}</label>
                        <label>{`${insuredCustomer.account?.name ?? '-'}`}</label>
                      </div>
                      <div className="right">
                        <label>{`${TABLE_HEADER_CONSTANTS.DATE_SUBMITTED} :`}</label>
                        <label>{`${new Date(insuredCustomer?.billedDate).toLocaleDateString('en-US') ?? '-'}`}</label>
                      </div>
                    </div>
                    <div className="card-content-section">
                      <div className="left">
                        <label>{`${TABLE_HEADER_CONSTANTS.BULK_TRANSACTION_ID} :`}</label>
                        <label>{`$${centsToDollars(insuredCustomer?.insuredAmount) ?? '-'}`}</label>
                      </div>

                      <div className="left right">
                        <label>{`${TABLE_HEADER_CONSTANTS.INSURANCE_AMOUNT} :`}</label>
                        <label>{`${insuredCustomer.bulkOrderId ?? '-'}`}</label>
                      </div>

                      <div className="right">
                        <label>{`${TABLE_HEADER_CONSTANTS.INSURANCE_PREMIUM} :`}</label>
                        <label>{`$${centsToDollars(insuredCustomer?.insurancePremium) ?? '-'}`}</label>
                      </div>
                    </div>
                  </div>
                  <div className="action-button">
                    {
                      <ActionMenu
                        actionButtons={[
                          { name: `${BUTTON_CONSTANTS.VIEW_DETAIL}`, action: () => handleViewDetails(insuredCustomer) },
                        ]}
                      />
                    }
                  </div>
                </div>
              </div>
            );
          })
        )}
        {loading && <Loader addedSpace loaderSize="small" />}
      </div>
    </div>
  );
};

export default InsuredCustomersReport;
